import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  PageSection,
  PageSectionVariants,
  Pagination,
  Content,
  Divider,
  Select,
  SelectList,
  SelectOption,
  MenuToggle,
  Level,
  LevelItem
} from '@patternfly/react-core';
import { BREAKPOINTS } from "shared/constants/breakpoints";
import { SheetsToolbar } from "../features/sheets/components/SheetsToolbar";
import { useSheets } from "../features/sheets/hooks/useSheets";
import { AuthContext } from 'shared/context/AuthContext';
import { SheetTable } from '../features/sheets/components/SheetsTable';
import { SheetsGallery } from '../features/sheets/components/SheetsGallery';
import { SpinnerPage } from 'shared/components/spinner/page/spinnerPage'

/* ================= IKONY ================= */

import { DiLinux, DiWindows, DiJsBadge } from "react-icons/di";
import { IoPrintOutline } from "react-icons/io5";
import { FaPhp } from "react-icons/fa6";
import { BsRouter, BsWifi } from "react-icons/bs";

const Sheets = (props) => {
  const [SheetsData, setSheetsData] = useState(null);
  const [isLoadingQualification, setIsLoadingQualification] = useState(true);
  const [lastRenderView, setLastRenderView] = useState(null);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(100);

  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState('Grupy');

  const context = useContext(AuthContext);
  const location = useLocation();

  const [viewMode, setViewMode] = useState("table");

  const [isMobile, setIsMobile] = useState(
    () => window.innerWidth < BREAKPOINTS.mobile
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < BREAKPOINTS.mobile);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // 🔥 jeśli kwalifikacja dev → domyślnie cards
  useEffect(() => {
    if (!SheetsData) return;

    if (isMobile) {
      setViewMode("cards");
      return;
    }

    if (
      SheetsData.headers.type &&
      SheetsData.headers.type.toLowerCase().includes("cards")
    ) {
      setViewMode("cards");
    } else {
      setViewMode("table");
    }

  }, [SheetsData, isMobile]);
  
  const {
    sheets,
    search,
    setSearch,
    yearFilter,
    setYearFilter,
    sessionFilter,
    setSessionFilter,
    groupFilter,
    setGroupFilter,
    statusFilter,
    setStatusFilter,
    setSheets
  } = useSheets(SheetsData?.sheets || []);
  

  const filteredSheets = useMemo(() => {
    return sheets || [];
  }, [sheets]);
  
  const availableSessions = useMemo(() => {
    return [
      ...new Set(
        (SheetsData?.sheets || []).flatMap(sheet => sheet.obj.exam_month || [])
      )
    ];
  }, [SheetsData]);

  const availableGroups = useMemo(() => {
    const groups = new Set();
    (SheetsData?.sheets || []).forEach(sheet => {
      Object.values(sheet.obj.groups_completed || {}).forEach(g => {
        // ❌ pomijamy grupy zaczynające się od "ABS"
        if (!g.group_symbol?.toUpperCase().startsWith("ABS")) {
          groups.add(g.group_symbol);
        }
      });
    });
    return Array.from(groups);
  }, [SheetsData]);
    
  const availableYears = useMemo(() => {
    return [
      ...new Set(
        (SheetsData?.sheets || []).flatMap(sheet => sheet.obj.year || [])
      )
    ];
  }, [SheetsData]);
  /* ================= FETCH ================= */

  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      try {

        setIsLoadingQualification(true);
        // console.log(`${props.state.api_url}/ti/api/v1${props.state.renderView}?page=${page}&limit=${perPage}`)
        const response = await fetch(
          `${props.state.api_url}/ti/api/v1${props.state.renderView}?page=${page}&limit=${perPage}`,
          { signal: controller.signal }
        );

        if (!response.ok) throw new Error("API error");

        const raw = await response.json();
        const result = raw.results || raw.data || raw || {};

        setSheetsData({
          sheets: result.sheets || [],
          headers: result.headers || { fields: [] },
          qualification_name: result.qualification_name || "",
          qualification_symbol: result.qualification_symbol || "",
          execution_time: result.execution_time || 0,
          number_of_sheets_for_qualification:
            result.count || result.number_of_sheets_for_qualification || 0
        });

        setLastRenderView(props.state.renderView);

      } catch (err) {
        if (err.name !== "AbortError") console.error(err);
        setSheetsData(null);
      } finally {
        setIsLoadingQualification(false);
      }
    };

    load();

    return () => controller.abort();

  }, [props.state.renderView, page, perPage, location.pathname]);

  /* ================= PAGINATION ================= */

  const renderPagination = (variant = 'top') => {
    if (!SheetsData) return null;

    return (
      <Pagination
        isCompact
        itemCount={SheetsData.number_of_sheets_for_qualification}
        page={page}
        perPage={perPage}
        onSetPage={(_, value) => setPage(value)}
        onPerPageSelect={(_, value) => {
          setPage(1);
          setPerPage(value);
        }}
        variant={variant}
      />
    );
  };

  /* ================= GROUP SELECT ================= */

  const onToggleClick = () => setIsOpen(prev => !prev);
  const onSelect = (_, value) => {
    setSelected(value);
    setIsOpen(false);
  };

  const toggle = (toggleRef) => (
    <MenuToggle
      ref={toggleRef}
      onClick={onToggleClick}
      isExpanded={isOpen}
      style={{ width: '200px' }}
    >
      {selected}
    </MenuToggle>
  );

  let unique_groups = [];
  if (Array.isArray(SheetsData?.sheets)) {
    const arr = [];
    SheetsData.sheets.forEach(row => {
      if (row?.obj?.groups_completed) {
        Object.values(row.obj.groups_completed).forEach(item => {
          if (item?.group_symbol) arr.push(item.group_symbol);
        });
      }
    });
    // zachowujemy kolejność pierwszego wystąpienia
    unique_groups = arr.filter((value, index, self) => self.indexOf(value) === index);
  }

  const SelectGroupMenu = (
    <Select
      isOpen={isOpen}
      selected={selected}
      onSelect={onSelect}
      onOpenChange={setIsOpen}
      toggle={toggle}
    >
      <SelectList>
        {unique_groups.map(group => (
          <SelectOption key={group} value={group}>
            {group}
          </SelectOption>
        ))}
      </SelectList>
    </Select>
  );

  /* ================= IKONY ================= */

  const Icons = {
    "LinuxIcon": <DiLinux size={18} style={{marginRight: '5px', marginBottom: '-5px'}} />,
    "WindowsIcon": <DiWindows size={20} style={{marginRight: '5px', marginBottom: '-5px'}} />,
    "PhpIcon": <FaPhp size={24} style={{marginRight: '5px', marginBottom: '-8px'}} />,
    "JsIcon": <DiJsBadge size={17} style={{marginRight: '5px', marginBottom: '-6px'}} />,
    "PrinterIcon": <IoPrintOutline size={19} style={{marginRight: '5px', marginBottom: '-6px'}} />,
    "RouterIcon": <BsRouter size={20} style={{marginRight: '5px', marginBottom: '-8px'}} />,
    "WifiIcon": <BsWifi size={20} style={{marginRight: '5px', marginBottom: '-5px'}} />,
    // "HtmlIcon": <BiLogoHtml5 size={22} style={{marginRight: '5px', marginBottom: '-8px'}} />,
    // "CssIcon": <BiLogoCss3 size={22} style={{marginRight: '5px', marginBottom: '-8px'}} />,
    // "GimpIcon": <SiGimp size={22} style={{marginRight: '5px', marginBottom: '-8px'}} />,
    // "SqlIcon": <GrMysql size={22} style={{marginRight: '5px', marginBottom: '-8px'}} />,
    // "InkscapeIcon": <SiInkscape size={20} style={{marginRight: '5px', marginBottom: '-8px'}} />
  };

  /* ================= RENDER CONTROL ================= */

  if (isLoadingQualification || !SheetsData) {
    return (
      <SpinnerPage 
        themeVersion=""
        isLoaded={isLoadingQualification}
        textSpinner={location?.pathname.split("/")[4]?.toUpperCase()} 
        className=""
      />
    );
  }

  return (
    <PageSection variant={PageSectionVariants.light}>
      <Divider />
      <Level>
        <LevelItem>
          <Content className="pf-u-font-weight-bold">
            {SheetsData.qualification_symbol} - {SheetsData.qualification_name}
          </Content>
        </LevelItem>
        <LevelItem>
          {SheetsData.number_of_sheets_for_qualification > 10 &&
            renderPagination()
          }
        </LevelItem>
      </Level>
      <Divider />
      <br />
      <SheetsToolbar
        context={context}
        search={search}
        setSearch={setSearch}
        yearFilter={yearFilter}
        setYearFilter={setYearFilter}
        sessionFilter={sessionFilter}
        setSessionFilter={setSessionFilter}
        groupFilter={groupFilter}
        setGroupFilter={setGroupFilter}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        availableSessions={availableSessions}
        availableGroups={availableGroups}
        availableYears={availableYears}
        viewMode={viewMode}
        setViewMode={setViewMode}
        resultCount={filteredSheets.length}
      />
      {viewMode === "table" && (
        <SheetTable
          props={props}
          context={context}
          header={SheetsData.headers.fields}
          SheetsData={SheetsData}
          Icons={Icons}
          SelectGroupMenu={SelectGroupMenu}
          viewMode={viewMode}
          setViewMode={setViewMode}
          sheets={sheets}
          // setSheets={setSheetsData}
          setSheets={setSheets}
          groupFilter={groupFilter}
        />
      )}
      {viewMode === "cards" && (
        <SheetsGallery 
          props={props}
          context={context}
          header={SheetsData.headers.fields}
          SheetsData={SheetsData}
          Icons={Icons}
          SelectGroupMenu={SelectGroupMenu}
          viewMode={viewMode}
          setViewMode={setViewMode}
          sheets={sheets}
          setSheets={setSheets}
          groupFilter={groupFilter}
        />
      )}
      
      {SheetsData.number_of_sheets_for_qualification > 10 &&
        renderPagination("bottom")
      }
    </PageSection>
  );
};

export default Sheets;